import { useEffect } from "react";
import {
  Nature,
  PrimaryNature,
} from "@dimensional-engineering/dimensional-models";
import { Mixpanel } from "@/helpers/mixpanel";
import { Roboto_Mono } from "next/font/google";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

import styles from "./NatureDiscovery.module.css";

import { ProfileColorMap } from "@/components/shared/ColorMap";
import NatureDiscoveryIcon from "../NatureDiscoveryIcon";
import { natureData } from "@/assets/traitInfo/natureData";
import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";
import { useAuth } from "@/context/AuthContext";
import { SmallProfileImageWithNature } from "@/components/shared/SmallProfileImage/SmallProfileImage";
import { ForwardArrowDeltaScreen } from "../ArchetypeDiscovery/ArchetypeDiscoveryScreen";

const font = Roboto_Mono({ subsets: ["latin"] });

type Props = {
  primaryNature: PrimaryNature | null | undefined;
  onBoarding?: boolean;
};

type DeltaProps = {
  natureDeltaData?:
    | {
        previousNature: Nature;
        currentNature: Nature;
      }
    | null
    | undefined;
};

export function NatureDiscoveryStepOne(props: Props) {
  const opacity = useStaggeredOpacity(6);

  const { user } = useAuth();

  const nature = natureData?.find(
    (n) => n.slug === props.primaryNature?.nature
  );

  useEffect(() => {
    Mixpanel?.track(
      "Nature Discovered",
      {
        source: props.onBoarding ? "onboarding" : null,
      },
      user?.uid
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sharedStyle = {
    opacity: opacity.step2,
  };

  const primaryStyle = props.primaryNature
    ? { borderColor: ProfileColorMap[props.primaryNature.nature] }
    : { borderColor: "#55504F" };

  const finalStyle =
    opacity.step3 === 1
      ? { ...sharedStyle, ...primaryStyle }
      : { ...sharedStyle, borderColor: "#55504F" };

  return (
    <div className={styles.main}>
      <p style={{ opacity: opacity.step1 }} className={styles.natureText}>
        NATURE
      </p>
      <div style={finalStyle} className={styles.iconDiv}>
        <NatureDiscoveryIcon />
      </div>
      <p style={{ opacity: opacity.step4 }} className={styles.yourPrimNature}>
        You unlocked your Primary Nature:
      </p>
      <h1 style={{ opacity: opacity.step5 }} className={styles.nature}>
        {nature?.name}
      </h1>
      <p style={{ opacity: opacity.step6 }} className={styles.description}>
        {nature?.description}
      </p>
    </div>
  );
}

export function NatureDiscoveryDeltaView(props: DeltaProps) {
  const imageUrl = useSelector((state: RootState) => state.user.imageURL);

  const opacity = useStaggeredOpacity(3);

  const oldNature = natureData.find(
    (n) => n.slug === props.natureDeltaData?.previousNature
  );
  const newNature = natureData.find(
    (n) => n.slug === props.natureDeltaData?.currentNature
  );

  const changedText =
    oldNature?.slug === newNature?.slug ? "is unchanged." : "has changed.";

  return (
    <div className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: "1" }}
        className={styles.natureText}
      >
        NATURE
      </p>
      <div style={{ opacity: opacity.step2 }} className={styles.deltaDiv}>
        <div className={styles.deltaInner}>
          <SmallProfileImageWithNature
            imageUrl={imageUrl ?? ""}
            nature={oldNature?.slug as Nature}
            medium
          />
          <div className={styles.deltaInnerTextDiv}>
            <p className={styles.natureNameDelta}>{oldNature?.shortName}</p>
            <p className={`${styles.beforeAfterText} ${font.className}`}>
              BEFORE
            </p>
          </div>
        </div>

        <ForwardArrowDeltaScreen marginTop={60} />

        <div className={styles.deltaInner}>
          <div className={styles.deltaInner}>
            <SmallProfileImageWithNature
              imageUrl={imageUrl ?? ""}
              nature={newNature?.slug as Nature}
              medium
            />
            <div className={styles.deltaInnerTextDiv}>
              <p className={styles.natureNameDelta}>{newNature?.shortName}</p>
              <p className={`${styles.beforeAfterText} ${font.className}`}>
                NOW
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ opacity: opacity.step3 }} className={styles.deltaBottomDiv}>
        <p className={styles.natureDeltaChange}>Your Nature {changedText}</p>
        <p className={styles.deltaDescription}>{newNature?.description}</p>
      </div>
    </div>
  );
}
